'use client'

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './HeroSlider.module.css'
import ArrowRight from '@/assets/img/Icon_ChevronRight.svg'
import ArrowLeft from '@/assets/img/Icon_ChevronLeft.svg'
import { useTranslations } from 'next-intl';
import LinkButton, { LinkButtonSize } from '../Buttons/LinkButton/LinkButton';
import IconButton, { IconButtonKind } from '../Buttons/IconButton/IconButton';
import { useSwipeable } from 'react-swipeable';
import { GameBrief } from '@/interfaces/games_types';
import { preload } from 'react-dom';
import PEGI from '../PEGI/PEGI';
import ExtraLabel, { ExtraLabelTypes } from '../Label/ExtraLabel';
import { useUserStore } from '@/stores/useUserStore';
import { useDynamicSignUpURL } from '@/apiUtilities/queries/useDynamicSignUpURL';
import { FEATURES } from '@/utils/constants';

type SlideProps = {
	id: number;
	translation: number,
	game: GameBrief,
	isCurrent: boolean
}

function Slide({ id, translation, game, isCurrent }:SlideProps) {
	const videoSrc = game && game.trailer ? process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.trailer : undefined
	if(game && game.thumb_horizontal && id === 0) preload(process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horizontal.esc_200_url, { as: 'image', fetchPriority: 'high' });

	return(
		<div className={styles.slide} style={{ transform: `translateX(${translation}%)`}}>
			{isCurrent && <video playsInline src={undefined} autoPlay muted controls={false} loop preload={isCurrent ? 'auto' : 'none'} fetchPriority="high" poster={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + game.thumb_horizontal.esc_400_url}/>}
		</div>
	)
}

type HeroSliderProps = {
	games: GameBrief[]
}

export default function HeroSlider({ games }:HeroSliderProps) {
	const t = useTranslations('HeroSlider')
	const { userId } = useUserStore()
	const [currentIndex, setCurrentIndex] = useState(0)
	const autoSlideTimer = useRef<number | null>(null)

	function clearAutoSlideTimer() {
		if (autoSlideTimer.current !== null) {
			window.clearTimeout(autoSlideTimer.current)
			autoSlideTimer.current = null
		}
	}

	function resetAutoSlideTimer() {
		clearAutoSlideTimer()
		autoSlideTimer.current = window.setTimeout(() => {
			setCurrentIndex(prev => prev + 1)
		}, 7000)
	}

	function slideForward() {
		setCurrentIndex(prev => prev + 1)
		resetAutoSlideTimer()
	}

	function slideBack() {
		setCurrentIndex(prev => prev - 1)
		resetAutoSlideTimer()
	}

	const sliderTranslation = currentIndex * 100 * -1	

	function getCircular(arr:GameBrief[], index:number): [number, GameBrief] {
		const n = arr.length;
		const i = ((index % n) + n) % n; // asegura resultado positivo
		return [i, arr[i]];
	}

	const [currentGameIndex, currentGame] = getCircular(games, currentIndex)
	const handlers = useSwipeable({
		onSwipedRight: () => {
			slideBack()
		},
		onSwipedLeft: () => {
			slideForward()
		}
	})

	useEffect(() => {
		resetAutoSlideTimer()
		return () => {
			clearAutoSlideTimer()
		}
	}, [currentIndex])

	const isByol = currentGame.type === 'steam' || currentGame.type === 'epic' || currentGame.type === 'battlenet'
	const { data: dynamicSignUpURLData } = useDynamicSignUpURL({
		enabled: FEATURES.signUpExternal && FEATURES.signUpExternalDynamic
	})

	const signUpLink = useMemo(() => {
	    if (!FEATURES.signUp) return '/login'
		if (!FEATURES.signUpExternal) return '/#plans'
		if (!FEATURES.signUpExternalDynamic) {
			return process.env.NEXT_PUBLIC_FEATURE_SIGNUP_EXTERNAL_LINK ?? ''
		}
		return dynamicSignUpURLData?.url || ''
		}, [dynamicSignUpURLData])
	return (
		<div className={styles.main} {...handlers} data-testid="HeroSlider">
			<div className={styles.topGradient}></div>

			<div className={styles.slider} style={ { transform: `translateX(${sliderTranslation}%)`}} data-testid="HeroSlider_slider">
				{games.map((game, idx) => {
					let page = Math.floor(currentIndex / games.length)
					if(idx < currentIndex % games.length) page += 1

					if(idx + ((page + 1) * games.length) === currentIndex + 1) page += 1
					if(idx + (page - 1) * games.length === currentIndex - 1) page -= 1
					
					const translateX = page * games.length * 100;
					// Cálculo de isCurrent robusto para índices negativos
					function positiveMod(n: number, m: number) {
						return ((n % m) + m) % m;
					}
					const isCurrent = idx === positiveMod(currentIndex, games.length);
					return (
						<Slide id={idx} key={idx} translation={translateX} game={game} isCurrent={isCurrent}/>
					)
				})}
			</div>
			

			<div className={`${styles.sliderControls} ${userId ? styles.login : ''}`} data-testid="HeroSlider_sliderControls">
				<div className={styles.contentWrapper}>
					<div className={styles.content}>
						{!userId && FEATURES.signUp &&
						<ExtraLabel
							href={signUpLink}
							type={ExtraLabelTypes.CTA}
							content={!isByol ? t('defaultLabel') : t('byolLabel')}
							className={styles.gameLabel}
						/>
						}
						<h2 className={`${styles.title} title2`} data-testid="HeroSlider_currentGameTitle">{currentGame.title}</h2>
						<div className={styles.pegiWrapper} data-testid="Game_Pegi">
							<span data-testid="Game_gameNotAvailableInDeviceText">{t('minAge')}</span>
							<PEGI pegi={currentGame.min_age!} />
						</div>
					</div>

					<LinkButton data-testid="HeroSlider_moreInfoButton" className={styles.button} href={`/games/${currentGame.game_id}`} size={LinkButtonSize.LARGE}>{t('moreInfo')}</LinkButton>
				</div>
				
				<div className={styles.indicators} data-testid="HeroSlider_indicators">
					<IconButton title={'Arrow left'} data-testid="HeroSlider_indicators_prevArrow" className={`${styles.arrow} ${styles.left}`} icon={ArrowLeft} kind={IconButtonKind.EMPTY} onClick={slideBack} />
					{games.map((el, idx) => {
						return (
							<div data-testid="HeroSlider_indicators_dots" key={idx} className={`${styles.indicator} ${idx === currentGameIndex ? styles.current : ''}`}></div>
						)
					})}
					<IconButton title={'Arrow right'}data-testid="HeroSlider_indicators_nextArrow" className={`${styles.arrow}`} icon={ArrowRight} kind={IconButtonKind.EMPTY} onClick={slideForward} />
				</div>
			</div>
			<div className={styles.bottomGradient}></div>
		</div>
	)
}